import { convertLength } from '@turf/helpers';
import { t } from 'i18next';
import { isNull } from 'lodash-es';
import { UseFormSetValue } from 'react-hook-form';

import {
  geoFenceCircleOption,
  geofenceNoneOption,
  geofenceOptions,
  GeoFenceTypes,
} from '~constants/enums';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import { Geofence } from '~hooks/useSites/models';
import { ItemNameAndId } from '~types/ItemNameAndId';
import { Nullable } from '~types/Nullable';

import { SiteFormV2Fields } from './types';

export const geofenceToItemNameAndId = (geofence: Geofence) => {
  const { geofenceType, movingGeofence } = geofence;
  if (movingGeofence) {
    return ItemNameAndId.parse({
      name: t('geofence.equipment'),
      id: GeoFenceTypes.EQUIPMENT,
    });
  }
  return geofenceOptions.find((item) => item.id === geofenceType);
};

/**
 * Auto-promote None → Circle when a user fills in coordinates on an
 * integration-created site (no defaultSite lat/lon), using the company's
 * default site_radius. `shouldDirty: true` is required so the change flows
 * through the dirty-aware deparse in Site.deparseSiteFormV2Update — otherwise
 * the geofence is silently dropped from the PATCH.
 */
export const applyAutoPromoteToCircle = (
  setValue: UseFormSetValue<SiteFormV2Fields>,
  params: {
    watchedLatLon: string;
    defaultSiteLat: number | string | null | undefined;
    defaultSiteLng: number | string | null | undefined;
    watchedRadius: number | null | undefined;
    currentGeofenceTypeId: string | null | undefined;
    companySiteRadius: number | string | null | undefined;
  },
): boolean => {
  const {
    watchedLatLon,
    defaultSiteLat,
    defaultSiteLng,
    watchedRadius,
    currentGeofenceTypeId,
    companySiteRadius,
  } = params;
  const isCircleOrNone =
    currentGeofenceTypeId === GeoFenceTypes.CIRCLE ||
    currentGeofenceTypeId === geofenceNoneOption.id ||
    isNull(currentGeofenceTypeId);
  if (
    !watchedLatLon ||
    defaultSiteLat ||
    defaultSiteLng ||
    watchedRadius ||
    !isCircleOrNone ||
    !companySiteRadius
  ) {
    return false;
  }
  const radiusInFeet = parseFloat(
    convertLength(Number(companySiteRadius), 'meters', 'feet').toFixed(2),
  );
  setValue('geofenceType', geoFenceCircleOption, {
    shouldValidate: true,
    shouldDirty: true,
  });
  setValue('radius', radiusInFeet, {
    shouldValidate: true,
    shouldDirty: true,
  });
  return true;
};

/**
 * Re-seed `latLon` from the user's newly geocoded address (or fall back to
 * the original site's value, or clear it). `shouldDirty: true` is required
 * so the new coordinates flow through the dirty-aware deparse — otherwise
 * the user picks a new address but the lat/lon stay at the old value on
 * the server.
 */
export const resolveLatLonForAddress = (
  setValue: UseFormSetValue<SiteFormV2Fields>,
  params: {
    geoCodedAddress: Nullable<AddressItem>;
    defaultSiteLatLon: string | null | undefined;
  },
): void => {
  const { geoCodedAddress, defaultSiteLatLon } = params;
  if (geoCodedAddress) {
    setValue('latLon', `${geoCodedAddress.lat},${geoCodedAddress.lng}`, {
      shouldValidate: true,
      shouldDirty: true,
    });
  } else if (defaultSiteLatLon) {
    setValue('latLon', defaultSiteLatLon, {
      shouldValidate: true,
      shouldDirty: true,
    });
  } else {
    setValue('latLon', '', {
      shouldValidate: true,
      shouldDirty: true,
    });
  }
};

export const isCircleOrEquipmentGeofenceType = (
  geofenceTypeId: string | null | undefined,
): boolean =>
  geofenceTypeId === GeoFenceTypes.CIRCLE || geofenceTypeId === GeoFenceTypes.EQUIPMENT;

/**
 * Re-centers a Circle/Equipment geofence on the newly geocoded address so the
 * circle follows the pin instead of staying at the old location.
 *
 * `shouldDirty: true` is required — Site.deparseSiteFormV2Update only includes
 * dirty fields in the PATCH, so without it the corrected center is dropped.
 * `shouldValidate` is omitted (matching the equivalent circleCenter write in
 * SiteFormV2.tsx) so resetLatLon's address-driven latLon update is the only
 * one that triggers a schema validation pass.
 */
export const recenterCircleOrEquipmentGeofenceForAddress = (
  setValue: UseFormSetValue<SiteFormV2Fields>,
  params: {
    geoCodedAddress: Nullable<AddressItem>;
    geofenceTypeId: string | null | undefined;
  },
): void => {
  const { geoCodedAddress, geofenceTypeId } = params;
  if (!geoCodedAddress) {
    return;
  }
  if (!isCircleOrEquipmentGeofenceType(geofenceTypeId)) {
    return;
  }
  setValue(
    'circleCenter',
    { lat: geoCodedAddress.lat, lng: geoCodedAddress.lng },
    { shouldDirty: true },
  );
};

export const getErrorMapping = (fieldName: string, defaultMessage: string) => {
  if (fieldName?.includes('address')) {
    fieldName = 'address';
  }
  switch (fieldName) {
    case 'address':
      return {
        field: 'address',
        message: defaultMessage,
      };
    default:
      return {
        field: fieldName,
        message: defaultMessage,
      };
  }
};
